import { useTranslation, I18nextProvider } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

export function useI18n() {
  const { t } = useTranslation()
  return (key: string, vars?: Record<string, unknown>) =>
    t(key, vars as any) as string
}

export function useScopedI18n(scope: string) {
  const { t } = useTranslation()
  return (key: string, vars?: Record<string, unknown>) =>
    t(`${scope}.${key}`, vars as any) as string
}

export function useCurrentLocale() {
  const { i18n } = useTranslation()
  return i18n.language === 'en' ? 'en' : 'ar'
}

export function useChangeLocale(opts?: { preserveSearchParams?: boolean }) {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  return (lng: string) => {
    // i18next is a singleton initialised once from the URL on first load and is
    // NOT re-created on client navigation, so switch the language here for the
    // text to update.
    void i18n.changeLanguage(lng)

    // Re-render the route via client-side navigation (NO hard reload): swap the
    // leading /ar|/en segment so react-router re-matches the new locale branch
    // and the page re-renders. Preserve the rest of the path, the query string
    // (when asked) and the hash.
    const { pathname, search, hash } = location
    const hasLocale = /^\/(ar|en)(\/|$)/.test(pathname)
    const newPathname = hasLocale
      ? pathname.replace(/^\/(ar|en)/, `/${lng}`)
      : `/${lng}${pathname}`
    const newSearch = opts?.preserveSearchParams ? search : ''
    navigate(`${newPathname}${newSearch}${hash}`)
  }
}

export { I18nextProvider as I18nProviderClient }
